import React, { useMemo, useState } from "react";
import { LOCALES } from "./i18n";
import ruloPicture from "./assets/rulo_picture.png";

export default function CoilWeightCalculator({ t, lang }) {
    const MATERIALS = useMemo(
        () => [
            { key: "steel", name: t.matSteel, density: 7850 },
            { key: "stainless", name: t.matStainless, density: 8000 },
            { key: "aluminum", name: t.matAluminum, density: 2700 },
            { key: "copper", name: t.matCopper, density: 8960 },
            { key: "brass", name: t.matBrass, density: 8500 },
            { key: "cast_iron", name: t.matCastIron, density: 7200 },
            { key: "pp", name: t.matPP, density: 905 },
            { key: "pe", name: t.matPE, density: 950 },
            { key: "pvc", name: t.matPVC, density: 1380 },
            { key: "custom", name: t.matCustom, density: null },
        ],
        [t]
    );

    const [materialKey, setMaterialKey] = useState("");
    const [density, setDensity] = useState("");
    const [thicknessMm, setThicknessMm] = useState("");
    const [widthMm, setWidthMm] = useState("");
    const [innerDiameterMm, setInnerDiameterMm] = useState("");
    const [outerDiameterMm, setOuterDiameterMm] = useState("");
    const [weightKg, setWeightKg] = useState("");
    const [lengthM, setLengthM] = useState("");
    const [weightPerMmWidth, setWeightPerMmWidth] = useState("");
    const [error, setError] = useState("");

    const selectedMaterial = MATERIALS.find((m) => m.key === materialKey);

    const parseNumber = (value) => {
        const normalized = String(value ?? "")
            .trim()
            .replace(/\s+/g, "")
            .replace(",", ".");

        if (!normalized) return NaN;

        const number = Number(normalized);
        return Number.isFinite(number) ? number : NaN;
    };

    const formatNumber = (value, decimals = 2) => {
        if (!Number.isFinite(value)) return "";

        const rounded = Number(value.toFixed(decimals));

        return rounded.toLocaleString(LOCALES[lang] || "tr-TR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals,
        });
    };

    const clearCalculated = () => {
        setError("");
    };

    const onSelectMaterial = (key) => {
        setMaterialKey(key);

        const material = MATERIALS.find((m) => m.key === key);

        if (!material) return;

        if (material.key === "custom") {
            setDensity("");
        } else {
            setDensity(String(material.density));
        }

        setError("");
    };

    const reset = () => {
        setMaterialKey("");
        setDensity("");
        setThicknessMm("");
        setWidthMm("");
        setInnerDiameterMm("");
        setOuterDiameterMm("");
        setWeightKg("");
        setLengthM("");
        setWeightPerMmWidth("");
        setError("");
    };

    const validateRequired = ({ dens, thickness, width, innerDiameter }) => {
        if (!Number.isFinite(dens) || dens <= 0) {
            return t.errDensity;
        }

        if (!Number.isFinite(thickness) || thickness <= 0) {
            return t.errThickness;
        }

        if (!Number.isFinite(width) || width <= 0) {
            return t.errWidth;
        }

        if (!Number.isFinite(innerDiameter) || innerDiameter <= 0) {
            return t.errInnerDia;
        }

        return "";
    };

    const calculateFromOuterDiameter = ({ dens, thickness, width, innerDiameter, outerDiameter }) => {
        if (!Number.isFinite(outerDiameter) || outerDiameter <= 0) {
            throw new Error(t.errOuterDia);
        }

        if (outerDiameter <= innerDiameter) {
            throw new Error(t.errOuterGtInner);
        }

        const rOutM = outerDiameter / 2000;
        const rInM = innerDiameter / 2000;
        const widthM = width / 1000;

        const volumeM3 = Math.PI * (rOutM * rOutM - rInM * rInM) * widthM;
        const weight = volumeM3 * dens;

        const length =
            (Math.PI * (outerDiameter * outerDiameter - innerDiameter * innerDiameter)) /
            (4 * thickness * 1000);

        const kgPerMm = weight / width;

        return { outerDiameter, weight, length, kgPerMm };
    };

    const calculateFromWeight = ({ dens, thickness, width, innerDiameter, weight }) => {
        if (!Number.isFinite(weight) || weight <= 0) {
            throw new Error(t.errWeight);
        }

        const widthM = width / 1000;
        const rInM = innerDiameter / 2000;

        const rOutM = Math.sqrt(weight / (dens * Math.PI * widthM) + rInM * rInM);
        const outerDiameter = rOutM * 2000;

        const length = (weight * 1000) / (dens * thickness * width);
        const kgPerMm = weight / width;

        return { outerDiameter, weight, length, kgPerMm };
    };

    const calculateFromLength = ({ dens, thickness, width, innerDiameter, length }) => {
        if (!Number.isFinite(length) || length <= 0) {
            throw new Error(t.errLength);
        }

        const weight = (length * dens * thickness * width) / 1000;

        const outerDiameter = Math.sqrt(
            (4 * thickness * length * 1000) / Math.PI +
            innerDiameter * innerDiameter
        );

        const kgPerMm = weight / width;

        return { outerDiameter, weight, length, kgPerMm };
    };

    const calculateFromWeightPerMmWidth = ({
                                               dens,
                                               thickness,
                                               width,
                                               innerDiameter,
                                               kgPerMm,
                                           }) => {
        if (!Number.isFinite(kgPerMm) || kgPerMm <= 0) {
            throw new Error(t.errKgPerMm);
        }

        const weight = kgPerMm * width;

        return calculateFromWeight({
            dens,
            thickness,
            width,
            innerDiameter,
            weight,
        });
    };

    const calculate = () => {
        setError("");

        const dens = parseNumber(density);
        const thickness = parseNumber(thicknessMm);
        const width = parseNumber(widthMm);
        const innerDiameter = parseNumber(innerDiameterMm);
        const outerDiameter = parseNumber(outerDiameterMm);
        const weight = parseNumber(weightKg);
        const length = parseNumber(lengthM);
        const kgPerMm = parseNumber(weightPerMmWidth);

        const requiredError = validateRequired({
            dens,
            thickness,
            width,
            innerDiameter,
        });

        if (requiredError) {
            setError(requiredError);
            return;
        }

        const filledOptionals = [
            Number.isFinite(outerDiameter) && outerDiameter > 0,
            Number.isFinite(weight) && weight > 0,
            Number.isFinite(length) && length > 0,
            Number.isFinite(kgPerMm) && kgPerMm > 0,
        ].filter(Boolean).length;

        if (filledOptionals === 0) {
            setError(t.errNeedOne);
            return;
        }

        if (filledOptionals === 4) {
            setError(t.errAllFilled);
            return;
        }

        try {
            let result;

            if (Number.isFinite(outerDiameter) && outerDiameter > 0) {
                result = calculateFromOuterDiameter({
                    dens,
                    thickness,
                    width,
                    innerDiameter,
                    outerDiameter,
                });
            } else if (Number.isFinite(weight) && weight > 0) {
                result = calculateFromWeight({
                    dens,
                    thickness,
                    width,
                    innerDiameter,
                    weight,
                });
            } else if (Number.isFinite(length) && length > 0) {
                result = calculateFromLength({
                    dens,
                    thickness,
                    width,
                    innerDiameter,
                    length,
                });
            } else {
                result = calculateFromWeightPerMmWidth({
                    dens,
                    thickness,
                    width,
                    innerDiameter,
                    kgPerMm,
                });
            }

            setOuterDiameterMm(formatNumber(result.outerDiameter, 2));
            setWeightKg(formatNumber(result.weight, 2));
            setLengthM(formatNumber(result.length, 2));
            setWeightPerMmWidth(formatNumber(result.kgPerMm, 4));
        } catch (err) {
            setError(err.message || t.errCalc);
        }
    };

    const isDensityLocked =
        selectedMaterial && selectedMaterial.key && selectedMaterial.key !== "custom";

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.cardRow}>
                <div style={styles.inner}>
                <div style={styles.title}>{t.coilTitle}</div>

                <div style={styles.row}>
                    <div style={styles.label}>{t.material}</div>
                    <div style={styles.unit}></div>
                    <div style={styles.inputWrap}>
                        <select
                            value={materialKey}
                            onChange={(e) => onSelectMaterial(e.target.value)}
                            style={styles.select}
                        >
                            <option value="" disabled>
                                {t.select}
                            </option>
                            {MATERIALS.map((material) => (
                                <option key={material.key} value={material.key}>
                                    {material.name}
                                    {material.density ? ` (${material.density})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <Field
                    label={t.density}
                    unit="kg/m³"
                    value={density}
                    disabled={isDensityLocked}
                    onChange={(v) => {
                        setDensity(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.thickness}
                    unit="mm"
                    value={thicknessMm}
                    onChange={(v) => {
                        setThicknessMm(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.width}
                    unit="mm"
                    value={widthMm}
                    onChange={(v) => {
                        setWidthMm(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.innerDiameter}
                    unit="mm"
                    value={innerDiameterMm}
                    onChange={(v) => {
                        setInnerDiameterMm(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.outerDiameter}
                    unit="mm"
                    value={outerDiameterMm}
                    onChange={(v) => {
                        setOuterDiameterMm(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.weight}
                    unit="kg"
                    value={weightKg}
                    onChange={(v) => {
                        setWeightKg(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label={t.length}
                    unit="m"
                    value={lengthM}
                    onChange={(v) => {
                        setLengthM(v);
                        clearCalculated();
                    }}
                />

                <Field
                    label="kg/mm"
                    unit=""
                    value={weightPerMmWidth}
                    onChange={(v) => {
                        setWeightPerMmWidth(v);
                        clearCalculated();
                    }}
                />

                {error ? <div style={styles.error}>{error}</div> : null}

                <div style={styles.actions}>
                    <button type="button" onClick={reset} style={styles.btnSecondary}>
                        {t.clear}
                    </button>

                    <button type="button" onClick={calculate} style={styles.btnPrimary}>
                        {t.calculate}
                    </button>
                </div>
                </div>

                {/* Rulo görseli - ölçü çizgileri ve etiketler görsel üzerinde */}
                <div style={styles.pictureWrap}>
                    <img src={ruloPicture} alt="" style={styles.picture} />
                    <svg viewBox="0 0 791 573" preserveAspectRatio="none" style={styles.overlay}>
                        <g stroke="#1d1d1b" strokeWidth="2.5" fill="none">
                            {/* Genişlik - gövde üstü açılı bracket */}
                            <path d="M 305 72 L 300 54 L 558 76 L 562 94" />
                            {/* Dış Çap - sağda dikey bracket */}
                            <path d="M 714 68 L 742 68 L 742 468 L 714 468" />
                            {/* İç Çap - delik kenarında dikey bracket */}
                            <path d="M 560 196 L 586 196 L 586 344 L 560 344" />
                            <path d="M 522 270 L 584 270" />
                            {/* Kalınlık - şerit kalınlık işareti (uzunluk çizgisiyle aynı açıda) */}
                            <path d="M 287 487 L 314 481 M 290 501 L 317 495 M 300 484 L 304 498" />
                            {/* Uzunluk - şerit altı çapraz bracket */}
                            <path d="M 148 550 L 140 572 L 624 462 L 616 440" />
                        </g>
                    </svg>
                    <span style={{ ...styles.imgLabel, left: "54%", top: "4.5%" }}>{t.width}</span>
                    <span style={{ ...styles.imgLabel, left: "96%", top: "44%" }}>{t.outerDiameter}</span>
                    <span style={{ ...styles.imgLabel, left: "58%", top: "47%" }}>{t.innerDiameter}</span>
                    <span style={{ ...styles.imgLabel, left: "38%", top: "78.5%" }}>{t.thickness}</span>
                    <span style={{ ...styles.imgLabel, left: "46%", top: "96%" }}>{t.length}</span>
                </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, unit, value, onChange, disabled = false }) {
    return (
        <div style={styles.row}>
            <div style={styles.label}>{label}</div>
            <div style={styles.unit}>{unit}</div>
            <div style={styles.inputWrap}>
                <input
                    inputMode="decimal"
                    placeholder="—"
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        ...styles.input,
                        ...(disabled ? styles.inputDisabled : {}),
                    }}
                />
            </div>
        </div>
    );
}

const styles = {
    page: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        padding: "8px 0",
    },

    card: {
        width: "100%",
        background: "#ffffff",
        borderRadius: 16,
        padding: 24,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    },

    // Form solda, rulo görseli sağda
    cardRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 32,
        alignItems: "center",
    },

    inner: {
        flex: "1 1 380px",
        maxWidth: 640,
    },

    pictureWrap: {
        position: "relative",
        flex: "0 1 420px",
        maxWidth: 420,
        alignSelf: "center",
    },

    picture: {
        width: "100%",
        height: "auto",
        display: "block",
    },

    overlay: {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
    },

    imgLabel: {
        position: "absolute",
        transform: "translate(-50%, -50%)",
        fontSize: 12,
        fontWeight: 700,
        color: "#1d1d1b",
        whiteSpace: "nowrap",
        textShadow: "0 0 4px #fff, 0 0 8px #fff",
        pointerEvents: "none",
    },

    title: {
        fontSize: 16,
        fontWeight: 700,
        marginBottom: 12,
        color: "#222",
    },

    row: {
        display: "grid",
        gridTemplateColumns: "1.15fr 0.7fr 1.25fr",
        gap: 10,
        alignItems: "center",
        padding: "10px 12px",
        borderRadius: 12,
        background: "#f2f2f2",
        marginBottom: 10,
    },

    label: {
        fontSize: 14,
        color: "#555",
        fontWeight: 600,
    },

    unit: {
        fontSize: 13,
        color: "#777",
        textAlign: "left",
        fontWeight: 600,
    },

    inputWrap: {
        display: "flex",
        justifyContent: "flex-end",
    },

    input: {
        width: "100%",
        height: 38,
        borderRadius: 10,
        border: "1px solid #e1e1e1",
        padding: "0 12px",
        fontSize: 14,
        outline: "none",
        background: "#fff",
        textAlign: "right",
        boxSizing: "border-box",
    },

    inputDisabled: {
        background: "#f8f8f8",
        color: "#666",
    },

    select: {
        width: "100%",
        height: 38,
        borderRadius: 10,
        border: "1px solid #e1e1e1",
        padding: "0 12px",
        fontSize: 14,
        outline: "none",
        background: "#fff",
        boxSizing: "border-box",
    },

    actions: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        marginTop: 12,
    },

    btnSecondary: {
        height: 44,
        borderRadius: 12,
        border: "1px solid #ddd",
        background: "#ececec",
        fontWeight: 700,
        color: "#666",
        cursor: "pointer",
    },

    btnPrimary: {
        height: 44,
        borderRadius: 12,
        border: "none",
        background: "#ff5a00",
        fontWeight: 800,
        color: "#fff",
        cursor: "pointer",
    },

    error: {
        marginTop: 8,
        padding: "10px 12px",
        background: "#fff1f0",
        border: "1px solid #ffccc7",
        borderRadius: 12,
        color: "#a8071a",
        fontSize: 13,
        fontWeight: 600,
    },
};
